import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Mail, Lock, User, Loader2, Eye, EyeOff, ArrowRight, Check, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthView = "login" | "signup" | "forgot-password";

interface PasswordStrength {
  is_valid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
  requirements: {
    min_length: number;
    requires_lowercase: boolean;
    requires_uppercase: boolean;
    requires_digit: boolean;
    requires_special: boolean;
  };
}

const loginSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string().min(6, "הסיסמה חייבת להכיל לפחות 6 תווים"),
});

// Enhanced signup schema with stronger password requirements
const signupSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string()
    .min(8, "הסיסמה חייבת להכיל לפחות 8 תווים")
    .regex(/[a-z]/, "הסיסמה חייבת להכיל אות קטנה באנגלית")
    .regex(/[A-Z]/, "הסיסמה חייבת להכיל אות גדולה באנגלית")
    .regex(/[0-9]/, "הסיסמה חייבת להכיל מספר")
    .regex(/[!@#$%^&*(),.?":{}|<>]/, "הסיסמה חייבת להכיל תו מיוחד"),
  fullName: z.string().min(2, "שם מלא חייב להכיל לפחות 2 תווים").max(100, "שם ארוך מדי"),
});

const forgotPasswordSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
});

export const AuthModal = ({ isOpen, onClose }: AuthModalProps) => {
  const { t } = useTranslation();
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const [view, setView] = useState<AuthView>("login");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);

  // Validate password strength when password changes during signup
  useEffect(() => {
    if (view === "signup" && formData.password.length > 0) {
      const validatePassword = async () => {
        try {
          const { data, error } = await supabase.rpc('validate_password_strength', {
            password: formData.password
          });
          if (!error && data) {
            setPasswordStrength(data as unknown as PasswordStrength);
          }
        } catch (err) {
          // Fallback to client-side validation
          const hasLower = /[a-z]/.test(formData.password);
          const hasUpper = /[A-Z]/.test(formData.password);
          const hasDigit = /[0-9]/.test(formData.password);
          const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
          const isLongEnough = formData.password.length >= 8;
          
          setPasswordStrength({
            is_valid: hasLower && hasUpper && hasDigit && hasSpecial && isLongEnough,
            errors: [],
            strength: isLongEnough && hasLower && hasUpper && hasDigit && hasSpecial ? 'strong' :
                     isLongEnough && hasLower && hasUpper && hasDigit ? 'medium' : 'weak',
            requirements: {
              min_length: 8,
              requires_lowercase: true,
              requires_uppercase: true,
              requires_digit: true,
              requires_special: true
            }
          });
        }
      };
      
      const debounceTimer = setTimeout(validatePassword, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setPasswordStrength(null);
    }
  }, [formData.password, view]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      if (view === "forgot-password") {
        forgotPasswordSchema.parse({ email: formData.email });
      } else if (view === "login") {
        loginSchema.parse(formData);
      } else {
        signupSchema.parse(formData);
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            newErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(newErrors);
        return;
      }
    }

    setIsLoading(true);

    try {
      if (view === "forgot-password") {
        const { error } = await resetPassword(formData.email);
        if (error) {
          toast.error("שגיאה בשליחת המייל", {
            description: error.message,
          });
        } else {
          toast.success("נשלח מייל לאיפוס סיסמה!", {
            description: "בדוק את תיבת הדואר שלך",
          });
          setView("login");
          resetForm();
        }
      } else if (view === "login") {
        const { error } = await signIn(formData.email, formData.password);
        if (error) {
          toast.error("שגיאה בהתחברות", {
            description: "אימייל או סיסמה שגויים",
          });
        } else {
          toast.success("התחברת בהצלחה!");
          onClose();
          resetForm();
        }
      } else {
        const { error } = await signUp(formData.email, formData.password, formData.fullName);
        if (error) {
          toast.error("שגיאה בהרשמה", {
            description: error.message,
          });
        } else {
          toast.success("נרשמת בהצלחה!", {
            description: "ברוך הבא ל-TravelBoost!",
          });
          onClose();
          resetForm();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        toast.error("שגיאה בהתחברות עם Google", {
          description: error.message,
        });
      }
      // Google OAuth redirects, so no need to close modal
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: "", password: "", fullName: "" });
    setErrors({});
  };

  const switchView = (newView: AuthView) => {
    setView(newView);
    setErrors({});
  };

  const getTitle = () => {
    switch (view) {
      case "login":
        return "ברוך הבא!";
      case "signup":
        return "הצטרף אלינו!";
      case "forgot-password":
        return "שחזור סיסמה";
    }
  };

  const getDescription = () => {
    switch (view) {
      case "login":
        return "התחבר כדי לנהל את הטיולים שלך";
      case "signup":
        return "צור חשבון כדי לשמור את ההעדפות שלך";
      case "forgot-password":
        return "הזן את האימייל שלך ונשלח לך קישור לאיפוס";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-white/95 backdrop-blur-xl border border-white/30 shadow-2xl">
        <DialogHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg mx-auto">
              <Plane className="h-8 w-8 text-primary-foreground rotate-[-35deg]" />
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold text-center">
            {getTitle()}
          </DialogTitle>
          <p className="text-muted-foreground text-sm">
            {getDescription()}
          </p>
        </DialogHeader>

        {view === "forgot-password" ? (
          /* Forgot Password Form */
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email" className="text-sm font-medium">
                אימייל
              </Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pr-10"
                  dir="ltr"
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold h-12 text-base"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin ml-2" />
                  שולח...
                </>
              ) : (
                "שלח קישור לאיפוס"
              )}
            </Button>

            <button
              type="button"
              onClick={() => switchView("login")}
              className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowRight className="h-4 w-4" />
              חזרה להתחברות
            </button>
          </form>
        ) : (
          /* Login/Signup Form */
          <>
            {/* Google Sign In Button */}
            <div className="py-2">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 gap-3 font-medium border-2 hover:bg-muted/50"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading || isLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-5 w-5">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                המשך עם Google
              </Button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">או</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {view === "signup" && (
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium">
                    שם מלא
                  </Label>
                  <div className="relative">
                    <User className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="ישראל ישראלי"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="pr-10"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.fullName && (
                    <p className="text-xs text-destructive">{errors.fullName}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  אימייל
                </Label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pr-10"
                    dir="ltr"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    סיסמה
                  </Label>
                  {view === "login" && (
                    <button
                      type="button"
                      onClick={() => switchView("forgot-password")}
                      className="text-xs text-primary hover:underline"
                    >
                      שכחת סיסמה?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pr-10 pl-10"
                    dir="ltr"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password}</p>
                )}
                
                {/* Password Requirements - Always visible during signup */}
                {view === "signup" && (
                  <div className="space-y-2 mt-2 p-3 bg-muted/50 rounded-lg border border-border/50">
                    <div className="flex items-center gap-2 text-xs font-medium text-foreground mb-2">
                      <Shield className="h-3.5 w-3.5 text-primary" />
                      דרישות סיסמה:
                    </div>
                    
                    {/* Strength Bar - only show when typing */}
                    {formData.password.length > 0 && passwordStrength && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-1.5 bg-background rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${
                              passwordStrength.strength === 'strong' ? 'w-full bg-green-500' :
                              passwordStrength.strength === 'medium' ? 'w-2/3 bg-yellow-500' :
                              'w-1/3 bg-red-500'
                            }`}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          passwordStrength.strength === 'strong' ? 'text-green-600' :
                          passwordStrength.strength === 'medium' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {passwordStrength.strength === 'strong' ? 'חזקה' :
                           passwordStrength.strength === 'medium' ? 'בינונית' : 'חלשה'}
                        </span>
                      </div>
                    )}
                    
                    {/* Requirements Checklist - Always visible */}
                    <div className="grid grid-cols-2 gap-1.5 text-xs">
                      <div className={`flex items-center gap-1.5 transition-colors ${
                        formData.password.length === 0 ? 'text-muted-foreground' :
                        formData.password.length >= 8 ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {formData.password.length === 0 ? (
                          <div className="h-3 w-3 rounded-full border border-current" />
                        ) : formData.password.length >= 8 ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        8+ תווים
                      </div>
                      <div className={`flex items-center gap-1.5 transition-colors ${
                        formData.password.length === 0 ? 'text-muted-foreground' :
                        /[a-z]/.test(formData.password) ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {formData.password.length === 0 ? (
                          <div className="h-3 w-3 rounded-full border border-current" />
                        ) : /[a-z]/.test(formData.password) ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        אות קטנה (a-z)
                      </div>
                      <div className={`flex items-center gap-1.5 transition-colors ${
                        formData.password.length === 0 ? 'text-muted-foreground' :
                        /[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {formData.password.length === 0 ? (
                          <div className="h-3 w-3 rounded-full border border-current" />
                        ) : /[A-Z]/.test(formData.password) ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        אות גדולה (A-Z)
                      </div>
                      <div className={`flex items-center gap-1.5 transition-colors ${
                        formData.password.length === 0 ? 'text-muted-foreground' :
                        /[0-9]/.test(formData.password) ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {formData.password.length === 0 ? (
                          <div className="h-3 w-3 rounded-full border border-current" />
                        ) : /[0-9]/.test(formData.password) ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        מספר (0-9)
                      </div>
                      <div className={`flex items-center gap-1.5 transition-colors col-span-2 ${
                        formData.password.length === 0 ? 'text-muted-foreground' :
                        /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {formData.password.length === 0 ? (
                          <div className="h-3 w-3 rounded-full border border-current" />
                        ) : /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        תו מיוחד (!@#$%^&*...)
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold h-12 text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin ml-2" />
                    מעבד...
                  </>
                ) : view === "login" ? (
                  "התחבר"
                ) : (
                  "צור חשבון"
                )}
              </Button>
            </form>

            <div className="text-center pb-2">
              <button
                type="button"
                onClick={() => switchView(view === "login" ? "signup" : "login")}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {view === "login" ? (
                  <>
                    אין לך חשבון? <span className="text-primary font-medium">הירשם עכשיו</span>
                  </>
                ) : (
                  <>
                    כבר יש לך חשבון? <span className="text-primary font-medium">התחבר</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
